import { z } from 'zod';
import { Confidence } from './entities.js';
import { SourceRef } from './ask.js';
/**
 * One link in an investigation's evidence chain (frontend spec §6.5).
 * Each item is provenance-attached: the optional `source` carries the
 * SourceRef used to render <SourceBadge>, and `confidence` (0–1) drives
 * the per-row <ConfidenceMeter> + the rolled-up hypothesis confidence.
 */
export const EvidenceItem = z.object({
    id: z.string(),
    source: SourceRef.optional(),
    claim: z.string().min(1),
    confidence: Confidence,
    createdBy: z.string(),
    createdAt: z.string(),
});
/**
 * One actionable next step in the case file. Completed entries render
 * struck-through but stay in place — the log is the audit trail.
 */
export const RecommendedAction = z.object({
    id: z.string(),
    body: z.string().min(1),
    completed: z.boolean().default(false),
});
/**
 * Append-only collaboration log entry. Every mutation to an investigation
 * appends one of these so co-analysts have a forensic trail of who did
 * what and when.
 */
export const CollaborationEntry = z.object({
    id: z.string(),
    actorUid: z.string(),
    actorName: z.string().optional(),
    action: z.enum([
        'created',
        'updated-hypothesis',
        'added-evidence',
        'removed-evidence',
        'added-entity',
        'removed-entity',
        'added-action',
        'completed-action',
        'archived',
        'unarchived',
        'confidence-override',
    ]),
    body: z.string().optional(),
    at: z.string(),
});
/**
 * The investigation case file (frontend spec §6.5). One Firestore doc per
 * investigation. Sub-arrays (`evidence`, `actions`, `log`) are kept on the
 * parent doc rather than as subcollections — for v0.1 the volume per case
 * file is small (dozens, not thousands), and a single doc keeps the realtime
 * subscription cheap and atomic. If a single case file grows past ~500 KB
 * we'll split into subcollections.
 */
export const Investigation = z.object({
    id: z.string(),
    orgId: z.string(),
    title: z.string().min(1),
    hypothesis: z.string().default(''),
    entities: z.array(z.string()).default([]),
    evidence: z.array(EvidenceItem).default([]),
    actions: z.array(RecommendedAction).default([]),
    log: z.array(CollaborationEntry).default([]),
    confidenceOverride: Confidence.optional(),
    archived: z.boolean().default(false),
    ownerUid: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
